/**
 * RealtimeVoiceSession.js — Redesigned Voice UI
 * Inspired by: Apple Siri / Google Assistant
 * Design: Warm, immersive, mobile-first, state-reactive orb
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, MicOff, Volume2, VolumeX, ShoppingCart, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── State configuration (colors + labels) ──────────────────────────────── */
const STATES = {
  idle:         { color: '#D97706', bg: 'rgba(217,119,6,0.12)',   label: 'جاهز',          labelEn: 'Ready',        anim: 'none' },
  connecting:   { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  label: 'جاري الاتصال',   labelEn: 'Connecting',   anim: 'voiceSpin 1.4s linear infinite' },
  listening:    { color: '#0EA5E9', bg: 'rgba(14,165,233,0.12)',  label: 'استمع…',         labelEn: 'Listening…',   anim: 'voicePulse 2s ease infinite' },
  thinking:     { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  label: 'يفكّر…',         labelEn: 'Thinking…',    anim: 'voiceSpin 1s linear infinite' },
  speaking:     { color: '#EA580C', bg: 'rgba(234,88,12,0.12)',   label: 'يتكلم…',         labelEn: 'Speaking…',    anim: 'voiceBreathe 1.2s ease-in-out infinite' },
  error:        { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   label: 'خطأ',            labelEn: 'Error',        anim: 'none' },
  disconnected: { color: '#A8A29E', bg: 'rgba(168,162,158,0.08)', label: 'انقطع الاتصال',  labelEn: 'Disconnected', anim: 'none' },
};

/* ─── Animated Orb ────────────────────────────────────────────────────────── */
function VoiceOrb({ status, color: primaryColor, siteLogoUrl, avatarSize = 140 }) {
  const st = STATES[status] || STATES.idle;
  const c = st.color;
  const isListening = status === 'listening';
  const isThinking  = status === 'thinking';
  const isSpeaking  = status === 'speaking';
  const isActive    = isListening || isThinking || isSpeaking;

  return (
    <div style={{ position: 'relative', width: avatarSize, height: avatarSize, flexShrink: 0 }}>
      {/* Outer pulse rings (listening only) */}
      {isListening && [1, 2, 3].map(i => (
        <div key={i} style={{
          position: 'absolute',
          inset: -(i * 18),
          borderRadius: '50%',
          border: `1.5px solid ${c}`,
          opacity: 0,
          animation: `voiceRing 2s ease-out ${i * 0.5}s infinite`,
          pointerEvents: 'none',
        }} />
      ))}

      {/* Speaking rings */}
      {isSpeaking && [1, 2].map(i => (
        <div key={i} style={{
          position: 'absolute',
          inset: -(i * 14),
          borderRadius: '50%',
          border: `2px solid ${c}`,
          opacity: 0,
          animation: `voiceRing 1s ease-out ${i * 0.3}s infinite`,
          pointerEvents: 'none',
        }} />
      ))}

      {/* Thinking spinner ring */}
      {isThinking && (
        <div style={{
          position: 'absolute',
          inset: -6,
          borderRadius: '50%',
          border: `3px solid transparent`,
          borderTopColor: c,
          borderRightColor: c + '60',
          animation: 'voiceSpin 0.9s linear infinite',
          pointerEvents: 'none',
        }} />
      )}

      {/* Main orb */}
      <div style={{
        width: avatarSize,
        height: avatarSize,
        borderRadius: '50%',
        background: isActive ? `radial-gradient(circle at 35% 35%, ${c}25, ${c}0A)` : `radial-gradient(circle at 35% 35%, ${primaryColor}18, ${primaryColor}06)`,
        border: `2px solid ${isActive ? c + '50' : primaryColor + '25'}`,
        boxShadow: isActive
          ? `0 0 0 1px ${c}20, 0 8px 40px ${c}30, inset 0 1px 1px rgba(255,255,255,0.8)`
          : `0 4px 24px ${primaryColor}15, inset 0 1px 1px rgba(255,255,255,0.8)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: isSpeaking ? 'voiceBreathe 1.4s ease-in-out infinite' : 'none',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Inner highlight */}
        <div style={{
          position: 'absolute', top: 8, left: 12, width: 28, height: 14,
          borderRadius: '50%', background: 'rgba(255,255,255,0.45)',
          pointerEvents: 'none',
        }} />

        {/* Avatar / logo */}
        {siteLogoUrl ? (
          <img
            src={siteLogoUrl.startsWith('/') ? `${API.replace('/api', '')}${siteLogoUrl}` : siteLogoUrl}
            alt=""
            style={{ width: avatarSize * 0.65, height: avatarSize * 0.65, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: avatarSize * 0.42, lineHeight: 1 }}>👨‍🍳</span>
        )}
      </div>

      {/* Mic indicator dot */}
      {isListening && (
        <div style={{
          position: 'absolute', bottom: 4, right: 4,
          width: 18, height: 18, borderRadius: '50%',
          background: '#0EA5E9', border: '2.5px solid #FFFFFF',
          boxShadow: '0 0 8px rgba(14,165,233,0.6)',
          animation: 'voiceDotPulse 1s ease infinite',
        }} />
      )}
    </div>
  );
}

/* ─── Word-by-word typewriter text ───────────────────────────────────────── */
function TypewriterText({ text, color, fontSize = '1.4rem', fontWeight = '500' }) {
  const [displayed, setDisplayed] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const wordsRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!text) { setDisplayed(''); setWordIdx(0); wordsRef.current = []; return; }
    const words = text.split(' ').filter(Boolean);
    wordsRef.current = words;
    // Only restart if we have a genuinely new response (text got shorter = new message)
    if (text.length < displayed.length + 15) {
      setDisplayed('');
      setWordIdx(0);
    }
  }, [text]); // eslint-disable-line

  useEffect(() => {
    const words = wordsRef.current;
    if (wordIdx >= words.length) return;
    timerRef.current = setTimeout(() => {
      setDisplayed(words.slice(0, wordIdx + 1).join(' '));
      setWordIdx(prev => prev + 1);
    }, 65); // ~65ms per word — natural reading pace
    return () => clearTimeout(timerRef.current);
  }, [wordIdx]);

  // Cursor blink while streaming
  const showCursor = displayed.length < (text?.length || 0) - 5;

  return (
    <div style={{
      textAlign: 'center', color: '#292524',
      fontSize, fontWeight, lineHeight: 1.5,
      fontFamily: "'Tajawal', sans-serif",
    }}>
      {displayed}
      {showCursor && (
        <span style={{ display: 'inline-block', width: 2, height: '1.2em', background: color, marginRight: 3, verticalAlign: 'text-bottom', animation: 'voiceBlink 0.7s step-end infinite' }} />
      )}
    </div>
  );
}

/* ─── Cart Slide-Up Panel ─────────────────────────────────────────────────── */
function CartPanel({ cart, color, isAr, onClose }) {
  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 40 }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(3px)' }} />
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: '#FFFFFF', borderRadius: '28px 28px 0 0',
          padding: '20px 24px 36px',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.12)',
          maxHeight: '65vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#E7E5E4', margin: '0 auto 20px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCart style={{ width: 18, height: 18, color }} />
            <span style={{ fontWeight: 700, fontSize: 16, color: '#1C1917', fontFamily: "'Tajawal', sans-serif" }}>
              {isAr ? 'طلبك' : 'Your Order'}
            </span>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', background: '#F5F5F4', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X style={{ width: 14, height: 14, color: '#78716C' }} />
          </button>
        </div>

        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#A8A29E', fontSize: 14 }}>
            {isAr ? 'لا يوجد أصناف بعد' : 'No items yet'}
          </div>
        ) : (
          <>
            {cart.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #F5F5F4' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1917', fontFamily: "'Tajawal', sans-serif" }}>{item.name}</div>
                  <div style={{ fontSize: 12, color: '#A8A29E', marginTop: 2 }}>x{item.quantity}</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color }}>
                  {(item.price * item.quantity).toFixed(0)} {isAr ? 'ر.س' : 'SAR'}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: `2px solid ${color}20` }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1C1917', fontFamily: "'Tajawal', sans-serif" }}>
                {isAr ? 'الإجمالي' : 'Total'}
              </span>
              <span style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Tajawal', sans-serif" }}>
                {total.toFixed(2)} {isAr ? 'ر.س' : 'SAR'}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Mini Cart Strip (always visible when items in cart) ───────────────── */
function MiniCartStrip({ cart, color, isAr, onClick }) {
  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = cart.reduce((s, i) => s + i.quantity, 0);
  if (cart.length === 0) return null;
  return (
    <button
      onClick={onClick}
      data-testid="mini-cart-strip"
      style={{
        margin: '0 16px 6px', display: 'flex', alignItems: 'center', gap: 10,
        background: '#FFFFFF', borderRadius: 18, padding: '10px 16px',
        border: `1.5px solid ${color}20`, boxShadow: `0 2px 12px ${color}10`,
        cursor: 'pointer', animation: 'voiceSlideUp .35s ease',
        flexShrink: 0, width: 'calc(100% - 32px)',
      }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 12, background: `${color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <ShoppingCart style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ flex: 1, textAlign: isAr ? 'right' : 'left' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1917' }}>
          {count} {isAr ? 'صنف' : 'item'}{count > 1 && !isAr ? 's' : ''}
        </div>
        <div style={{ fontSize: 11, color: '#A8A29E' }}>
          {cart.map(i => i.name).slice(0, 3).join('، ')}{cart.length > 3 ? '...' : ''}
        </div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color, fontFamily: "'Tajawal', sans-serif", flexShrink: 0 }}>
        {total.toFixed(0)} {isAr ? 'ر.س' : 'SAR'}
      </div>
    </button>
  );
}

/* ─── Quick Suggestion Chips ────────────────────────────────────────────── */
function QuickSuggestions({ status, isAr, color, cart }) {
  if (status !== 'listening' && status !== 'idle' && status !== 'disconnected') return null;

  const hasItems = cart.length > 0;
  const arSuggestions = hasItems
    ? ['راجع طلبي', 'أضف مشروب', 'بس كذا، خلص', 'غيّر الكمية']
    : ['وش عندكم؟', 'أبي أطلب', 'وش أحلى شي؟', 'فيه عروض؟'];
  const enSuggestions = hasItems
    ? ['Review my order', 'Add a drink', 'That\'s all', 'Change quantity']
    : ['What do you have?', 'I want to order', 'What\'s popular?', 'Any deals?'];

  const suggestions = isAr ? arSuggestions : enSuggestions;

  return (
    <div
      data-testid="quick-suggestions"
      style={{
        display: 'flex', gap: 6, padding: '0 16px 8px',
        overflowX: 'auto', flexShrink: 0, WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
      }}
    >
      {suggestions.map((s, i) => (
        <div
          key={i}
          style={{
            padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            whiteSpace: 'nowrap', flexShrink: 0,
            background: `${color}08`, border: `1px solid ${color}20`,
            color: '#57534E', fontFamily: "'Tajawal', sans-serif",
            animation: `voiceFadeUp .3s ease ${i * 0.06}s both`,
          }}
        >
          {s}
        </div>
      ))}
    </div>
  );
}

/* ─── Action Toast (shows when tool is executed) ────────────────────────── */
function ActionToast({ message, color }) {
  if (!message) return null;
  return (
    <div
      data-testid="action-toast"
      style={{
        position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)',
        background: '#FFFFFF', borderRadius: 16, padding: '8px 16px',
        boxShadow: `0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px ${color}15`,
        display: 'flex', alignItems: 'center', gap: 8, zIndex: 30,
        animation: 'voiceFadeUp .3s ease, voiceFadeUp .3s ease 2.5s reverse forwards',
        maxWidth: '80%',
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: '#1C1917', fontFamily: "'Tajawal', sans-serif" }}>
        {message}
      </span>
    </div>
  );
}

/* ─── Rating Stars (shows at end of conversation) ──────────────────────── */
function RatingStars({ rating, onRate, isAr, color }) {
  return (
    <div
      data-testid="rating-stars"
      style={{
        margin: '0 16px 8px', background: '#FFFFFF', borderRadius: 20,
        padding: '14px 20px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        textAlign: 'center', animation: 'voiceSlideUp .4s ease',
        flexShrink: 0,
      }}
    >
      <p style={{ fontSize: 13, fontWeight: 600, color: '#1C1917', marginBottom: 10 }}>
        {isAr ? 'كيف كانت تجربتك؟' : 'How was your experience?'}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={() => onRate(star)}
            data-testid={`rate-star-${star}`}
            style={{
              width: 40, height: 40, borderRadius: '50%', border: 'none',
              background: star <= rating ? `${color}15` : '#F5F5F4',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all .2s', transform: star <= rating ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            <span style={{ fontSize: 20, filter: star <= rating ? 'none' : 'grayscale(1) opacity(0.3)' }}>
              &#9733;
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────────────── */
export default function RealtimeVoiceSession({
  branchId, siteLogoUrl, rName, color = '#D97706', isAr = true, onClose, sessionId,
}) {
  const [status, setStatus]         = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [agentText, setAgentText]   = useState('');
  const [cart, setCart]             = useState([]);
  const [cartOpen, setCartOpen]     = useState(false);
  const [muted, setMuted]           = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');
  const [events, setEvents]         = useState([]);
  const [pdplConsent, setPdplConsent] = useState(false);
  const [showConsent, setShowConsent] = useState(true);
  const [rating, setRating]         = useState(0);
  const [actionToast, setActionToast] = useState('');
  const [showRating, setShowRating] = useState(false);

  const pcRef        = useRef(null);
  const dcRef        = useRef(null);
  const audioRef     = useRef(null);
  const localStream  = useRef(null);
  const cartRef      = useRef([]);
  const customerRef  = useRef({ name: '', phone: '', address: '' });
  const isConnecting = useRef(false);
  const abortRef     = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => { cartRef.current = cart; }, [cart]);

  /* ── Log conversation to backend ────────────────────────────────────── */
  const logConversation = useCallback(async () => {
    const c = cartRef.current;
    const duration = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
    try {
      await axios.post(`${API}/voice-agent/log`, {
        branch_id: branchId,
        session_id: sessionId,
        events: events.map(e => ({ role: e.role, text: e.text })),
        cart_items: c.map(i => ({ name: i.name, price: i.price, quantity: i.quantity })),
        total_amount: c.reduce((s, i) => s + i.price * i.quantity, 0),
        customer_satisfied: rating >= 4 ? true : rating > 0 ? false : null,
        language: events.some(e => /[a-zA-Z]{3,}/.test(e.text || '')) ? 'en' : 'ar',
        duration_seconds: duration,
        pdpl_consent: pdplConsent,
      });
    } catch (err) {
      console.warn('Failed to log conversation:', err);
    }
  }, [branchId, sessionId, events, pdplConsent, rating]);

  /* ── Tool execution ─────────────────────────────────────────────────── */
  const executeTool = useCallback(async (name, args, callId) => {
    let result = '';
    try {
      if (name === 'add_item') {
        const { item_id, item_name, price, quantity = 1 } = args;
        setCart(prev => {
          const ex = prev.find(i => i.id === item_id);
          if (ex) return prev.map(i => i.id === item_id ? { ...i, quantity: i.quantity + quantity } : i);
          return [...prev, { id: item_id, name: item_name, price, quantity }];
        });
        result = JSON.stringify({ success: true, message: `تم إضافة ${item_name} (${quantity}x ${price} ر.س)` });
        setActionToast(`+ ${item_name} (${quantity}x)`);
        setTimeout(() => setActionToast(''), 2800);

      } else if (name === 'remove_item') {
        setCart(prev => prev.filter(i => i.id !== args.item_id));
        result = JSON.stringify({ success: true, message: 'تم الحذف من السلة' });
        setActionToast(isAr ? 'تم الحذف من السلة' : 'Removed from cart');
        setTimeout(() => setActionToast(''), 2800);

      } else if (name === 'view_cart') {
        const c = cartRef.current;
        const total = c.reduce((s, i) => s + i.price * i.quantity, 0);
        result = JSON.stringify({
          items: c.map(i => ({ name: i.name, quantity: i.quantity, price: i.price, subtotal: i.price * i.quantity })),
          total, count: c.reduce((s, i) => s + i.quantity, 0),
          message: c.length === 0 ? 'السلة فارغة' : `${c.length} صنف، الإجمالي ${total} ريال`,
        });

      } else if (name === 'save_customer_info') {
        const { customer_name, customer_phone, customer_address = '' } = args;
        customerRef.current = { name: customer_name || 'ضيف', phone: customer_phone || '0500000000', address: customer_address };
        result = JSON.stringify({ success: true, message: `تم حفظ: ${customer_name} - ${customer_phone}` });

      } else if (name === 'generate_checkout') {
        const c = cartRef.current;
        if (c.length === 0) {
          result = JSON.stringify({ success: false, message: 'السلة فارغة' });
        } else {
          const customer = customerRef.current;
          const res = await axios.post(`${API}/public/checkout/create`, {
            branch_id: branchId, session_id: sessionId,
            cart: c.map(i => ({ item_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
            customer_name: customer.name || 'ضيف',
            customer_phone: customer.phone || '0500000000',
            customer_address: customer.address || '',
            notes: args.notes || '',
          });
          const checkoutId = res.data?.checkout_id;
          const url = `${window.location.origin}/checkout/${checkoutId}`;
          setCheckoutUrl(url);
          result = JSON.stringify({ success: true, checkout_url: url, total: res.data?.total, message: `رابط الدفع جاهز! الإجمالي: ${res.data?.total} ريال` });
          // Show rating after successful checkout
          setTimeout(() => setShowRating(true), 3000);
        }

      } else if (name === 'get_recommendations') {
        const { current_item } = args;
        result = JSON.stringify({
          success: true,
          message: `اقتراحات مكمّلة لـ "${current_item}" متاحة. اقترح صنفاً واحداً بطريقة طبيعية.`,
          instruction: 'اقترح بلطف: "كثير من عملاءنا يحبون... معاه، تبي تجرب؟"',
        });

      } else if (name === 'handle_complaint') {
        const { complaint_type, details, severity = 'medium' } = args;
        // Store complaint in events for logging
        setEvents(prev => [...prev, { role: 'system', text: `[شكوى: ${complaint_type}] ${details} (${severity})` }]);
        result = JSON.stringify({
          success: true,
          message: severity === 'high'
            ? 'تم تسجيل الشكوى. يُرجى التصعيد لمسؤول.'
            : 'تم تسجيل الملاحظة. قدّم اعتذاراً وحلاً.',
        });

      } else if (name === 'rate_experience') {
        const r = args.rating || 5;
        setRating(r);
        result = JSON.stringify({ success: true, message: `شكراً! تم تسجيل التقييم: ${r}/5` });

      } else {
        result = JSON.stringify({ error: `أداة غير معروفة: ${name}` });
      }
    } catch (err) {
      result = JSON.stringify({ error: `خطأ: ${err.message}` });
    }

    if (dcRef.current?.readyState === 'open') {
      dcRef.current.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output: result } }));
      dcRef.current.send(JSON.stringify({ type: 'response.create' }));
    }
  }, [branchId, sessionId]);

  /* ── Data channel messages ──────────────────────────────────────────── */
  const agentTextRef = useRef('');
  const handleDcMessage = useCallback((event) => {
    let msg;
    try { msg = JSON.parse(event.data); } catch { return; }

    switch (msg.type) {
      case 'session.created':
      case 'session.updated':
        setStatus('listening');
        break;
      case 'input_audio_buffer.speech_started':
        setStatus('listening');
        setTranscript('');
        setAgentText('');
        agentTextRef.current = '';
        break;
      case 'input_audio_buffer.speech_stopped':
        setStatus('thinking');
        break;
      case 'conversation.item.input_audio_transcription.completed':
        setTranscript(msg.transcript || '');
        setEvents(prev => [...prev.slice(-6), { role: 'user', text: msg.transcript || '' }]);
        break;
      case 'response.created':
        setStatus('thinking');
        agentTextRef.current = '';
        setAgentText('');
        break;
      case 'response.audio_transcript.delta':
        agentTextRef.current += (msg.delta || '');
        setAgentText(agentTextRef.current);
        setStatus('speaking');
        break;
      case 'response.audio_transcript.done':
        setEvents(prev => [...prev.slice(-6), { role: 'assistant', text: msg.transcript || agentTextRef.current }]);
        break;
      case 'response.done':
        setStatus('listening');
        break;
      case 'response.function_call_arguments.done': {
        try { executeTool(msg.name, JSON.parse(msg.arguments || '{}'), msg.call_id); }
        catch { executeTool(msg.name, {}, msg.call_id); }
        break;
      }
      case 'error':
        console.error('RT error:', msg.error);
        setErrorMsg(msg.error?.message || 'خطأ غير معروف');
        setStatus('error');
        break;
      default: break;
    }
  }, [executeTool]);

  /* ── Disconnect ─────────────────────────────────────────────────────── */
  const disconnect = useCallback(() => {
    // Log conversation before disconnecting
    if (events.length > 0 && pdplConsent) logConversation();
    if (abortRef.current) { abortRef.current.aborted = true; abortRef.current = null; }
    isConnecting.current = false;
    if (dcRef.current) { try { dcRef.current.close(); } catch {} dcRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
    if (localStream.current) { localStream.current.getTracks().forEach(t => t.stop()); localStream.current = null; }
    if (audioRef.current) {
      audioRef.current.pause(); audioRef.current.srcObject = null;
      audioRef.current.src = ''; audioRef.current.load(); audioRef.current = null;
    }
    setStatus('disconnected');
  }, [events, pdplConsent, logConversation]);

  /* ── Connect ────────────────────────────────────────────────────────── */
  const connect = useCallback(async () => {
    if (isConnecting.current || pcRef.current) return;
    isConnecting.current = true;
    const abort = { aborted: false };
    abortRef.current = abort;

    setStatus('connecting'); setTranscript(''); setAgentText(''); setEvents([]); setErrorMsg('');
    agentTextRef.current = '';
    startTimeRef.current = Date.now();

    try {
      const sessRes = await axios.post(`${API}/public/realtime/session`, { branch_id: branchId, voice: 'verse' });
      if (abort.aborted) return;
      const ephemeralToken = sessRes.data?.client_secret?.value;
      if (!ephemeralToken) throw new Error('No ephemeral token');

      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun.cloudflare.com:3478' },
        ],
      });
      pcRef.current = pc;

      if (audioRef.current) { audioRef.current.pause(); audioRef.current.srcObject = null; }
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true; audioEl.playsInline = true; audioEl.setAttribute('playsinline', '');
      audioRef.current = audioEl;
      pc.ontrack = (e) => {
        const el = audioRef.current;
        if (el && e.streams[0]) { el.srcObject = e.streams[0]; el.play().catch(() => {}); }
      };

      if (abort.aborted) { pc.close(); pcRef.current = null; return; }

      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: { ideal: true }, noiseSuppression: { ideal: true }, autoGainControl: { ideal: true }, sampleRate: { ideal: 24000 }, channelCount: { exact: 1 } }
        });
        if (abort.aborted) { stream.getTracks().forEach(t => t.stop()); pc.close(); pcRef.current = null; return; }
      } catch {
        try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {}
      }
      localStream.current = stream;
      if (stream) {
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
      } else {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const dest = ctx.createMediaStreamDestination();
          osc.frequency.value = 0; osc.connect(dest);
          dest.stream.getTracks().forEach(t => pc.addTrack(t, dest.stream));
        } catch {}
      }

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = handleDcMessage;
      dc.onopen = () => {
        if (abort.aborted) return;
        setStatus('listening');
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            input_audio_transcription: { model: 'whisper-1' },
            turn_detection: { type: 'server_vad', threshold: 0.78, prefix_padding_ms: 500, silence_duration_ms: 1400 },
          },
        }));
      };
      dc.onclose = () => { if (!abort.aborted) setStatus('disconnected'); };
      dc.onerror = () => { if (!abort.aborted) setStatus('error'); };

      const offer = await pc.createOffer();
      if (abort.aborted) { pc.close(); pcRef.current = null; return; }
      await pc.setLocalDescription(offer);

      const negRes = await fetch(`${API}/public/realtime/negotiate`, {
        method: 'POST', body: offer.sdp,
        headers: { 'Content-Type': 'application/sdp', 'X-Ephemeral-Token': ephemeralToken },
      });
      if (abort.aborted) { pc.close(); pcRef.current = null; return; }
      if (!negRes.ok) throw new Error(`Negotiate failed: ${negRes.status}`);
      const { sdp } = await negRes.json();
      await pc.setRemoteDescription({ type: 'answer', sdp });

      pc.onconnectionstatechange = () => {
        if (abort.aborted) return;
        if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) setStatus('disconnected');
      };
    } catch (err) {
      if (!abort.aborted) {
        console.error('Connect error:', err);
        setErrorMsg(err.message || 'فشل الاتصال');
        setStatus('error');
        if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.srcObject = null; audioRef.current = null; }
      }
    } finally {
      isConnecting.current = false;
      if (abortRef.current === abort) abortRef.current = null;
    }
  }, [branchId, handleDcMessage]);

  /* ── Toggle mute ────────────────────────────────────────────────────── */
  const toggleMute = useCallback(() => {
    if (!localStream.current) return;
    localStream.current.getAudioTracks().forEach(t => { t.enabled = muted; });
    setMuted(m => !m);
  }, [muted]);

  const toggleSpeaker = useCallback(() => {
    if (audioRef.current) audioRef.current.muted = !speakerMuted;
    setSpeakerMuted(s => !s);
  }, [speakerMuted]);

  /* ── Lifecycle ──────────────────────────────────────────────────────── */
  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.aborted = true; disconnect(); };
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!showConsent) {
      const t = setTimeout(() => connect(), 60);
      return () => clearTimeout(t);
    }
  }, [showConsent]); // eslint-disable-line

  const handleConsentAccept = (consent) => {
    setPdplConsent(consent);
    setShowConsent(false);
  };

  /* ── Derived ────────────────────────────────────────────────────────── */
  const st = STATES[status] || STATES.idle;
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const isActive = ['listening', 'thinking', 'speaking'].includes(status);
  const canRetry = status === 'error' || status === 'disconnected';

  const CSS = `
    @keyframes voiceRing    { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2.2);opacity:0} }
    @keyframes voiceSpin    { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
    @keyframes voiceBreathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
    @keyframes voicePulse   { 0%,100%{box-shadow:0 0 0 0 var(--vc,#0EA5E9)} 50%{box-shadow:0 0 0 16px rgba(0,0,0,0)} }
    @keyframes voiceDotPulse{ 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(0.85)} }
    @keyframes voiceBlink   { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes voiceFadeUp  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    @keyframes voiceSlideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
    .vc-transcript{animation:voiceFadeUp .35s ease}
    .vc-agent{animation:voiceFadeUp .4s ease}
  `;

  /* ── PDPL Consent Screen ── */
  if (showConsent) {
    return (
      <div dir={isAr ? 'rtl' : 'ltr'} data-testid="pdpl-consent-screen" style={{
        position: 'fixed', inset: 0, zIndex: 300,
        maxWidth: 520, margin: '0 auto', right: 0,
        background: 'radial-gradient(circle at 50% 25%, #FFFFFF 0%, #FFF4E0 60%, #FFF9F0 100%)',
        fontFamily: "'Tajawal', 'Cairo', sans-serif",
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 24px',
      }}>
        {/* Close button */}
        <button
          onClick={() => { onClose(); }}
          data-testid="consent-close-btn"
          style={{ position: 'absolute', top: 16, [isAr ? 'left' : 'right']: 16, width: 40, height: 40, borderRadius: 14, background: 'rgba(120,113,108,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <X style={{ width: 18, height: 18, color: '#78716C' }} />
        </button>

        {/* Orb */}
        <VoiceOrb status="idle" color={color} siteLogoUrl={siteLogoUrl} avatarSize={100} />

        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C1917', marginTop: 24, textAlign: 'center' }}>
          {isAr ? `مرحباً بك في ${rName}` : `Welcome to ${rName}`}
        </h2>
        <p style={{ fontSize: 14, color: '#78716C', textAlign: 'center', marginTop: 8, lineHeight: 1.7, maxWidth: 360 }}>
          {isAr
            ? 'وكيل طلبات صوتي ذكي يساعدك في اختيار وطلب وجباتك المفضلة.'
            : 'A smart voice ordering agent to help you pick and order your favorite meals.'}
        </p>

        {/* PDPL Notice */}
        <div style={{
          marginTop: 24, padding: '16px 20px', borderRadius: 16,
          background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(120,113,108,0.12)',
          maxWidth: 380, width: '100%',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#0EA5E912', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 14 }}>&#128274;</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1C1917' }}>
              {isAr ? 'إشعار الخصوصية' : 'Privacy Notice'}
            </span>
          </div>
          <p style={{ fontSize: 12, color: '#78716C', lineHeight: 1.8 }}>
            {isAr
              ? 'قد يتم تسجيل هذه المحادثة لتحسين جودة الخدمة. يتم إخفاء هوية بياناتك الشخصية تلقائياً وفقاً لنظام حماية البيانات الشخصية (PDPL). يمكنك المتابعة بدون تسجيل.'
              : 'This conversation may be recorded to improve service quality. Your personal data is automatically anonymized per PDPL. You can proceed without recording.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 380 }}>
          <button
            onClick={() => handleConsentAccept(true)}
            data-testid="consent-accept-btn"
            style={{
              width: '100%', padding: '14px 24px', borderRadius: 20, border: 'none',
              background: `linear-gradient(135deg, ${color}, ${color}CC)`,
              color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 4px 16px ${color}40`,
              transition: 'transform 0.2s',
            }}
          >
            {isAr ? 'موافق، ابدأ المحادثة' : 'Agree & Start'}
          </button>
          <button
            onClick={() => handleConsentAccept(false)}
            data-testid="consent-skip-btn"
            style={{
              width: '100%', padding: '12px 24px', borderRadius: 20,
              border: '1px solid rgba(120,113,108,0.2)', background: 'transparent',
              color: '#78716C', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {isAr ? 'المتابعة بدون تسجيل' : 'Continue without recording'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} style={{
      position: 'fixed', inset: 0, zIndex: 300,
      maxWidth: 520, margin: '0 auto', right: 0,
      background: 'radial-gradient(circle at 50% 25%, #FFFFFF 0%, #FFF4E0 60%, #FFF9F0 100%)',
      fontFamily: "'Tajawal', 'Cairo', sans-serif",
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', userSelect: 'none',
    }}>
      <style>{CSS}</style>

      {/* ── Header ── */}
      <div style={{
        padding: '16px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        {/* Close */}
        <button
          onClick={() => { disconnect(); onClose(); }}
          style={{ width: 40, height: 40, borderRadius: 14, background: 'rgba(120,113,108,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s' }}
        >
          <X style={{ width: 18, height: 18, color: '#78716C' }} />
        </button>

        {/* Title */}
        <div style={{ textAlign: 'center', flex: 1, margin: '0 12px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1C1917', lineHeight: 1.2 }}>{rName}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginTop: 3 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: st.color, display: 'inline-block',
              animation: isActive ? 'voiceDotPulse 1.2s ease infinite' : 'none',
            }} />
            <span style={{ fontSize: 11, color: st.color, fontWeight: 600, letterSpacing: '0.04em' }}>
              {isAr ? st.label : st.labelEn}
            </span>
          </div>
        </div>

        {/* Cart badge */}
        <button
          onClick={() => setCartOpen(true)}
          style={{ width: 40, height: 40, borderRadius: 14, background: cartCount > 0 ? `${color}12` : 'rgba(120,113,108,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'all .2s' }}
        >
          <ShoppingCart style={{ width: 18, height: 18, color: cartCount > 0 ? color : '#A8A29E' }} />
          {cartCount > 0 && (
            <span style={{
              position: 'absolute', top: -3, right: -3,
              minWidth: 18, height: 18, borderRadius: 9,
              background: color, color: '#fff', fontSize: 10, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
              border: '2px solid #FFF9F0',
            }}>{cartCount}</span>
          )}
        </button>
      </div>

      {/* ── Main content ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '0 24px', gap: 0, position: 'relative', minHeight: 0,
      }}>

        {/* User transcript bubble — appears when user speaks */}
        <div style={{ minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28, width: '100%' }}>
          {transcript ? (
            <div className="vc-transcript" style={{
              background: 'rgba(255,255,255,0.75)',
              backdropFilter: 'blur(6px)',
              border: '1px solid rgba(120,113,108,0.12)',
              borderRadius: isAr ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
              padding: '10px 18px',
              fontSize: 15, color: '#57534E', lineHeight: 1.5,
              maxWidth: '88%',
              boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
              fontFamily: "'Tajawal', sans-serif",
            }}>
              "{transcript}"
            </div>
          ) : (
            <div style={{ height: 40 }} />
          )}
        </div>

        {/* ORB — the visual anchor */}
        <VoiceOrb
          status={status}
          color={color}
          siteLogoUrl={siteLogoUrl}
          avatarSize={136}
        />

        {/* Agent response text — word-by-word */}
        <div style={{ marginTop: 32, minHeight: 80, width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '0 8px' }}>
          {agentText ? (
            <TypewriterText
              text={agentText}
              color={st.color}
              fontSize="1.35rem"
              fontWeight="500"
            />
          ) : status === 'connecting' ? (
            <div style={{ textAlign: 'center', color: '#A8A29E', fontSize: 14 }}>
              {isAr ? 'جاري الاتصال بالوكيل…' : 'Connecting to agent…'}
            </div>
          ) : status === 'listening' && !transcript ? (
            <div style={{ textAlign: 'center', color: '#C1BAB4', fontSize: 15, animation: 'voiceFadeUp .4s ease' }}>
              {isAr ? 'تكلّم، أنا أسمعك 👂' : 'Speak, I\'m listening 👂'}
            </div>
          ) : status === 'error' ? (
            <div style={{ textAlign: 'center', color: '#EF4444', fontSize: 14, padding: '0 20px' }}>
              <AlertCircle style={{ width: 20, height: 20, marginBottom: 6, display: 'block', margin: '0 auto 6px' }} />
              {errorMsg || (isAr ? 'حدث خطأ في الاتصال' : 'Connection error')}
            </div>
          ) : (
            <div style={{ height: 48 }} />
          )}
        </div>

        {/* Last assistant message (subtle, fades after agent text) */}
        {!agentText && events.length > 0 && (
          <div style={{ marginTop: 12, maxWidth: '90%', textAlign: 'center', color: '#A8A29E', fontSize: 13, lineHeight: 1.6, fontFamily: "'Tajawal', sans-serif" }}>
            {events.findLast(e => e.role === 'assistant')?.text?.slice(0, 80)}
            {(events.findLast(e => e.role === 'assistant')?.text?.length || 0) > 80 ? '…' : ''}
          </div>
        )}
      </div>

      {/* ── Checkout banner — slides up from bottom ── */}
      {checkoutUrl && (
        <div style={{
          margin: '0 16px 8px',
          background: '#FFFFFF',
          borderRadius: 22,
          padding: '14px 18px',
          boxShadow: `0 4px 24px ${color}15, 0 1px 4px rgba(0,0,0,0.06)`,
          border: `1.5px solid ${color}25`,
          display: 'flex', alignItems: 'center', gap: 12,
          animation: 'voiceSlideUp .4s cubic-bezier(.16,1,.3,1)',
          flexShrink: 0,
        }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 22 }}>&#128179;</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1C1917' }}>
              {isAr ? 'رابط الدفع جاهز!' : 'Checkout Ready!'}
            </div>
            <div style={{ fontSize: 11, color: '#A8A29E', marginTop: 1 }}>
              {isAr ? 'اضغط لإتمام الطلب' : 'Tap to complete'}
            </div>
          </div>
          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" style={{
            padding: '10px 20px', borderRadius: 20, background: color,
            color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none',
            boxShadow: `0 3px 12px ${color}40`, flexShrink: 0,
          }}>
            {isAr ? 'ادفع' : 'Pay'}
          </a>
        </div>
      )}

      {/* ── Rating Stars (shows after checkout) ── */}
      {showRating && !cartOpen && (
        <RatingStars
          rating={rating}
          onRate={(r) => { setRating(r); setTimeout(() => setShowRating(false), 1500); }}
          isAr={isAr}
          color={color}
        />
      )}

      {/* ── Mini Cart Strip (always visible when items exist) ── */}
      {!checkoutUrl && !cartOpen && (
        <MiniCartStrip cart={cart} color={color} isAr={isAr} onClick={() => setCartOpen(true)} />
      )}

      {/* ── Quick Suggestion Chips ── */}
      <QuickSuggestions status={status} isAr={isAr} color={color} cart={cart} />

      {/* ── Action Toast ── */}
      <ActionToast message={actionToast} color={color} />

      {/* ── Controls ── */}
      <div style={{
        padding: '12px 32px',
        paddingBottom: 'max(28px, env(safe-area-inset-bottom, 28px))',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 32,
        flexShrink: 0,
      }}>
        {/* Mute mic */}
        <button
          onClick={toggleMute}
          style={{
            width: 52, height: 52, borderRadius: '50%',
            background: muted ? '#FEE2E2' : 'rgba(120,113,108,0.08)',
            border: `1.5px solid ${muted ? '#FCA5A5' : 'rgba(120,113,108,0.12)'}`,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .2s',
          }}
        >
          {muted
            ? <MicOff style={{ width: 20, height: 20, color: '#EF4444' }} />
            : <Mic style={{ width: 20, height: 20, color: '#78716C' }} />
          }
        </button>

        {/* Main center button */}
        <button
          onClick={canRetry ? connect : undefined}
          disabled={status === 'connecting'}
          style={{
            width: 80, height: 80, borderRadius: '50%', border: 'none', cursor: canRetry ? 'pointer' : 'default',
            background: canRetry
              ? `linear-gradient(135deg, ${color}, ${color}CC)`
              : status === 'connecting'
              ? '#E7E5E4'
              : '#FFFFFF',
            boxShadow: canRetry
              ? `0 8px 24px ${color}40`
              : status === 'connecting'
              ? 'none'
              : `0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 1px rgba(255,255,255,0.9)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .3s',
            position: 'relative',
            // Pulse ring while active
          }}
        >
          {/* Subtle active ring */}
          {isActive && !canRetry && (
            <div style={{
              position: 'absolute', inset: -8, borderRadius: '50%',
              border: `2px solid ${st.color}30`,
              animation: 'voiceRing 2s ease infinite',
              pointerEvents: 'none',
            }} />
          )}
          {status === 'connecting' ? (
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid #D1D5DB`, borderTopColor: color, animation: 'voiceSpin 0.8s linear infinite' }} />
          ) : canRetry ? (
            <RefreshCw style={{ width: 28, height: 28, color: '#fff' }} />
          ) : (
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: `radial-gradient(circle, ${st.color}AA, ${st.color}60)`,
              boxShadow: `0 0 12px ${st.color}60`,
              animation: isActive ? 'voiceBreathe 1.4s ease infinite' : 'none',
            }} />
          )}
        </button>

        {/* Speaker toggle */}
        <button
          onClick={toggleSpeaker}
          style={{
            width: 52, height: 52, borderRadius: '50%',
            background: speakerMuted ? '#FEE2E2' : 'rgba(120,113,108,0.08)',
            border: `1.5px solid ${speakerMuted ? '#FCA5A5' : 'rgba(120,113,108,0.12)'}`,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .2s',
          }}
        >
          {speakerMuted
            ? <VolumeX style={{ width: 20, height: 20, color: '#EF4444' }} />
            : <Volume2 style={{ width: 20, height: 20, color: '#78716C' }} />
          }
        </button>
      </div>

      {/* Hint text under controls */}
      {!canRetry && (
        <div style={{ textAlign: 'center', paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))', marginTop: -16, fontSize: 11, color: '#C1BAB4', fontWeight: 500 }}>
          {isAr
            ? (status === 'listening' ? '🎙 تحدث الآن' : status === 'speaking' ? '🔊 الوكيل يتكلم' : status === 'thinking' ? '⏳ يفكّر…' : 'وكيل طلبات صوتي')
            : (status === 'listening' ? '🎙 Speak now' : status === 'speaking' ? '🔊 Agent speaking' : status === 'thinking' ? '⏳ Thinking…' : 'Voice ordering agent')}
        </div>
      )}

      {/* Cart panel */}
      {cartOpen && <CartPanel cart={cart} color={color} isAr={isAr} onClose={() => setCartOpen(false)} />}
    </div>
  );
}
